import React, { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import portfolioService, { PortfolioHoldingsBreakdown } from '../services/portfolio';
import { getTopXAndOthers, CHART_COLORS, useIsMobile } from '../utils/chartHelpers';
import { useETFManagement } from '../hooks/useETFManagement';
import { useAssetManagement } from '../hooks/useAssetManagement';
import { useIsinLookup } from '../hooks/useIsinLookup';
import { usePortfolioData } from '../hooks/usePortfolioData';

import { usePrivacy } from '../contexts/PrivacyContext';
import { useCurrency } from '../contexts/CurrencyContext';

// Components
import PortfolioHeader from '../components/portfolio/PortfolioHeader';
import PortfolioTabNavigation, { PortfolioTab } from '../components/portfolio/PortfolioTabNavigation';
import PerformanceSection from '../components/portfolio/PerformanceSection';
import ETFInvestmentsSection from '../components/portfolio/ETFInvestmentsSection';
import DirectInvestmentsSection from '../components/portfolio/DirectInvestmentsSection';
import TypeSpecificDiversificationSection from '../components/portfolio/TypeSpecificDiversificationSection';
import TotalExposureSection from '../components/portfolio/TotalExposureSection';
import ETFOverlapSection from '../components/portfolio/ETFOverlapSection';
import AnalyticsTab from '../components/portfolio/AnalyticsTab';
import ChatSection from '../components/portfolio/ChatSection';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ChartContainer from '../components/ui/ChartContainer';
import InfoTooltip from '../components/ui/InfoTooltip';

const PortfolioDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const portfolioId = parseInt(id || '0', 10);
  const { isPrivateMode } = usePrivacy();
  const { formatAmount } = useCurrency();

  const isMobile = useIsMobile();

  // View state — honour ?tab= query param for deep-linking
  const validTabs: PortfolioTab[] = ['total', 'etfs', 'assets', 'performance', 'analytics', 'chat'];
  const tabParam = searchParams.get('tab') as PortfolioTab | null;
  const [activeTab, setActiveTab] = useState<PortfolioTab>(
    tabParam && validTabs.includes(tabParam) ? tabParam : 'total'
  );

  // ETF management hook
  const etfManagement = useETFManagement(portfolioId);

  // Asset management hook
  const assetManagement = useAssetManagement(portfolioId);

  // ISIN lookup hook
  const isinLookup = useIsinLookup(portfolioId);

  // Fetch portfolio details
  const { 
    data: portfolio, 
    isLoading: portfolioLoading, 
    error: portfolioError
  } = useQuery({
    queryKey: ['portfolio', portfolioId],
    queryFn: () => portfolioService.getPortfolio(portfolioId),
    enabled: !!portfolioId,
  });

  // Fetch available ETFs for dropdown
  const {
    data: etfs,
    isLoading: etfsLoading,
    error: etfsError
  } = useQuery({
    queryKey: ['etfs'],
    queryFn: () => portfolioService.getETFs(),
  });
  
  // Fetch combined portfolio holdings breakdown
  const {
    data: combinedHoldingsBreakdown,
    isLoading: combinedHoldingsBreakdownLoading,
    error: combinedHoldingsBreakdownError
  } = useQuery({
    queryKey: ['portfolio-holdings-breakdown', portfolioId],
    queryFn: () => portfolioService.getPortfolioHoldingsBreakdown(portfolioId),
    enabled: !!portfolioId,
  });

  // Fetch ETF-specific portfolio holdings breakdown
  const {
    data: etfHoldingsBreakdown,
    isLoading: etfHoldingsBreakdownLoading,
    error: etfHoldingsBreakdownError
  } = useQuery({
    queryKey: ['portfolio-etf-holdings-breakdown', portfolioId],
    queryFn: () => portfolioService.getPortfolioETFHoldingsBreakdown(portfolioId),
    enabled: !!portfolioId,
  });

  // Fetch asset-specific portfolio holdings breakdown
  const {
    data: assetHoldingsBreakdown,
    isLoading: assetHoldingsBreakdownLoading,
    error: assetHoldingsBreakdownError
  } = useQuery({
    queryKey: ['portfolio-asset-holdings-breakdown', portfolioId],
    queryFn: () => portfolioService.getPortfolioAssetHoldingsBreakdown(portfolioId),
    enabled: !!portfolioId,
  });

  // Fetch total securities exposure (direct + via ETFs)
  const {
    data: totalExposureData,
    isLoading: totalExposureLoading,
    error: totalExposureError
  } = useQuery({
    queryKey: ['portfolio-total-exposure', portfolioId],
    queryFn: () => portfolioService.getPortfolioTotalExposure(portfolioId),
    enabled: !!portfolioId && activeTab === 'total',
  });

  // Fetch ETF overlap data
  const {
    data: etfOverlapData,
    isLoading: etfOverlapLoading,
  } = useQuery({
    queryKey: ['portfolio-etf-overlap', portfolioId],
    queryFn: () => portfolioService.getPortfolioETFOverlap(portfolioId),
    enabled: !!portfolioId && activeTab === 'etfs',
  });

  // Fetch available assets for dropdown
  const {
    data: assets,
    isLoading: assetsLoading,
    error: assetsError
  } = useQuery({
    queryKey: ['assets'],
    queryFn: () => portfolioService.getAssets(),
  });

  // Use the portfolio data hook to calculate values
  const {
    etfAllocationData,
    assetAllocationData,
    totalEtfValue,
    totalAssetValue,
    totalPortfolioValue
  } = usePortfolioData(portfolio);

  // Concentration stats — pure look-through weight math, no new API calls
  const concentrationStats = useMemo(() => {
    if (!totalExposureData || totalExposureData.securities.length === 0) return null;
    const total = totalExposureData.total_portfolio_value;
    if (total <= 0) return null;
    const sorted = [...totalExposureData.securities].sort((a, b) => b.total_value - a.total_value);
    const weights = sorted.map(s => s.total_value / total);
    const hhi = weights.reduce((sum, w) => sum + w * w, 0);
    return {
      effectiveN: hhi > 0 ? Math.round(1 / hhi) : null,
      top5:  weights.slice(0,  5).reduce((s, w) => s + w, 0),
      top10: weights.slice(0, 10).reduce((s, w) => s + w, 0),
    };
  }, [totalExposureData]);

  // Prepare data for combined industry and country charts
  const transformBreakdownData = (breakdown: PortfolioHoldingsBreakdown | undefined) => {
    // Add debug logging
    console.log('Holdings breakdown data:', breakdown);
    
    if (!breakdown) {
      console.log('No breakdown data available');
      return { industryData: [], countryData: [] };
    }
    
    // Convert objects to arrays with name-value pairs
    const industries = breakdown.industries ? 
      Object.entries(breakdown.industries).map(([name, value]) => ({
        name,
        value: typeof value === 'number' ? value : Number(value)
      })) : [];

    const countries = breakdown.countries ? 
      Object.entries(breakdown.countries).map(([name, value]) => ({
        name,
        value: typeof value === 'number' ? value : Number(value)
      })) : [];

    // Log the extracted arrays
    console.log('Industries array:', industries);
    console.log('Countries array:', countries);

    const industryData = getTopXAndOthers(industries);
    const countryData = getTopXAndOthers(countries);

    // Log the transformed data
    console.log('Transformed industry data:', industryData);
    console.log('Transformed country data:', countryData);

    return { industryData, countryData };
  };

  // Transform Combined breakdown data for Total view
  const { 
    industryData: combinedIndustryData, 
    countryData: combinedCountryData 
  } = transformBreakdownData(combinedHoldingsBreakdown);

  // Transform ETF breakdown data
  const { 
    industryData: etfIndustryData, 
    countryData: etfCountryData 
  } = transformBreakdownData(etfHoldingsBreakdown);

  // Transform Asset breakdown data
  const { 
    industryData: assetIndustryData, 
    countryData: assetCountryData 
  } = transformBreakdownData(assetHoldingsBreakdown);

  // Determine if loading based on active tab
  const isLoading = 
    portfolioLoading || 
    etfsLoading || 
    assetsLoading || 
    (activeTab === 'total' && (combinedHoldingsBreakdownLoading || totalExposureLoading)) ||
    (activeTab === 'etfs' && etfHoldingsBreakdownLoading) || 
    (activeTab === 'assets' && assetHoldingsBreakdownLoading);

  // Determine if error based on active tab
  const hasError =
    !!portfolioError || 
    !!etfsError || 
    !!assetsError || 
    !portfolio || 
    (activeTab === 'total' && (!!combinedHoldingsBreakdownError || !!totalExposureError)) ||
    (activeTab === 'etfs' && !!etfHoldingsBreakdownError) || 
    (activeTab === 'assets' && !!assetHoldingsBreakdownError);

  if (isLoading) {
    return (
      <LoadingSpinner
        size="lg"
        text={`Loading ${activeTab === 'total' ? 'portfolio' : activeTab === 'etfs' ? 'ETF' : activeTab === 'assets' ? 'asset' : 'performance'} data...`}
      />
    );
  }

  if (hasError) {
    return <div className="text-center p-4 text-red-500">Error loading portfolio data</div>;
  }

  return (
    <div className="container-fluid py-4">
      {/* Portfolio header */}
      <PortfolioHeader 
        portfolio={portfolio} 
        totalPortfolioValue={totalPortfolioValue} 
      />

      {/* Investment type tabs */}
      <PortfolioTabNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Total Portfolio View */}
      {activeTab === 'total' && (
        <>
          {/* Row 1: Top Holdings donut + Security Exposure table */}
          <div className="row g-4 mb-4">
            <div className="col-12 col-lg-4">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-baseline mb-2">
                    <div className="section-label">Top Holdings</div>
                    <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}></span>
                  </div>
                  {totalExposureData && totalExposureData.securities.length > 0 ? (() => {
                    const TOP_N = 8;
                    const allSorted = [...totalExposureData.securities].sort((a, b) => b.total_value - a.total_value);
                    const top = allSorted.slice(0, TOP_N);
                    const rest = allSorted.slice(TOP_N);
                    const othersValue = rest.reduce((s, sec) => s + sec.total_value, 0);
                    const donutData = [
                      ...top.map(s => ({ name: s.identifier, fullName: s.name || s.identifier, value: s.total_value })),
                      ...(othersValue > 0 ? [{ name: `Others (${rest.length})`, fullName: `Others (${rest.length})`, value: othersValue }] : []),
                    ];
                    return (
                      <div style={{ flex: 1, minHeight: 200 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={donutData} cx="50%" cy="45%" innerRadius="35%" outerRadius="55%" paddingAngle={2} dataKey="value" labelLine={false}>
                              {donutData.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                const pct = ((d.value / totalExposureData.total_portfolio_value) * 100).toFixed(1);
                                return (
                                  <div style={{ background: '#fff', border: '1px solid #f4f4f5', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }}>
                                    <div style={{ fontWeight: 600, color: '#18181b', marginBottom: '2px' }}>{d.fullName}</div>
                                    <div style={{ color: '#71717a' }}>{isPrivateMode ? `${pct}%` : `${formatAmount(d.value, false)} (${pct}%)`}</div>
                                  </div>
                                );
                              }}
                            />
                            <Legend
                              layout="horizontal"
                              align="center"
                              verticalAlign="bottom"
                              formatter={(value, entry: any) => {
                                const pct = ((entry.payload.value / totalExposureData.total_portfolio_value) * 100).toFixed(1);
                                return <span style={{ fontSize: '0.72rem', color: '#52525b' }}>{value} <span style={{ color: '#a1a1aa' }}>{pct}%</span></span>;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })() : (
                    <div style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>No data available</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <TotalExposureSection
                exposureData={totalExposureData}
                isLoading={totalExposureLoading}
              />
            </div>
          </div>

          {/* Row 2: Portfolio Summary + Sector + Region */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-4">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4 d-flex flex-column justify-content-between">
                  <div>
                    <div className="section-label mb-3">Total Value</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: '#000537', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '20px' }}>
                      {formatAmount(totalPortfolioValue, isPrivateMode)}
                    </div>
                  </div>
                  <div>
                    {totalPortfolioValue > 0 && (
                      <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ width: `${(totalAssetValue / totalPortfolioValue) * 100}%`, background: '#000537' }} />
                        <div style={{ width: `${(totalEtfValue / totalPortfolioValue) * 100}%`, background: '#8c9fd4' }} />
                      </div>
                    )}
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#000537', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.82rem', color: '#71717a' }}>Direct</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(totalAssetValue, isPrivateMode)}</span>
                          {totalPortfolioValue > 0 && <span style={{ fontSize: '0.72rem', color: '#a1a1aa', width: '36px', textAlign: 'right' }}>{((totalAssetValue / totalPortfolioValue) * 100).toFixed(1)}%</span>}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#8c9fd4', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.82rem', color: '#71717a' }}>ETFs</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(totalEtfValue, isPrivateMode)}</span>
                          {totalPortfolioValue > 0 && <span style={{ fontSize: '0.72rem', color: '#a1a1aa', width: '36px', textAlign: 'right' }}>{((totalEtfValue / totalPortfolioValue) * 100).toFixed(1)}%</span>}
                        </div>
                      </div>
                    </div>
                    {concentrationStats && (
                      <>
                        <div style={{ height: '1px', background: '#f4f4f5', margin: '14px 0' }} />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span style={{ fontSize: '0.72rem', color: '#a1a1aa' }}>Concentration</span>
                          <InfoTooltip text={<>
                            <div style={{ marginBottom: '6px' }}><strong>Effective holdings</strong> — 1 / Herfindahl index.</div>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', background: '#f4f4f5', borderRadius: '4px', padding: '3px 6px', marginBottom: '6px', color: '#18181b' }}>N_eff = 1 / Σwᵢ²</div>
                            <div style={{ color: '#71717a', marginBottom: '8px' }}>Equivalent number of equal-weight positions with the same concentration. Lower = more concentrated.</div>
                            <div style={{ marginBottom: '4px' }}><strong>Top 5 / Top 10 weight</strong> — combined look-through weight of the largest names.</div>
                            <div style={{ color: '#71717a' }}>A direct read on single-name tail risk regardless of how many ETFs you hold.</div>
                          </>} />
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontSize: '0.82rem', color: '#71717a' }}>Effective holdings</span>
                            <span style={{ fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>{concentrationStats.effectiveN ?? '—'}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontSize: '0.82rem', color: '#71717a' }}>Top 5 weight</span>
                            <span style={{ fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>{(concentrationStats.top5 * 100).toFixed(1)}%</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontSize: '0.82rem', color: '#71717a' }}>Top 10 weight</span>
                            <span style={{ fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>{(concentrationStats.top10 * 100).toFixed(1)}%</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex justify-content-between align-items-baseline mb-4">
                    <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>Sector</span>
                  </div>
                  {combinedHoldingsBreakdownLoading ? (
                    <LoadingSpinner size="sm" text="Loading..." />
                  ) : combinedIndustryData.length > 0 ? (() => {
                    const max = combinedIndustryData[0]?.value ?? 1;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {combinedIndustryData.map(item => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '160px', flexShrink: 0, fontSize: '0.82rem', color: '#52525b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
                            <div style={{ flex: 1, height: '6px', background: '#f4f4f5', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${max > 0 ? (item.value / max) * 100 : 0}%`, height: '100%', background: '#000537', borderRadius: '3px', transition: 'width 0.4s ease' }} />
                            </div>
                            <span style={{ width: '48px', flexShrink: 0, textAlign: 'right', fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{(item.value * 100).toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    );
                  })() : (
                    <div style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>No industry data available</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex justify-content-between align-items-baseline mb-4">
                    <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>Region</span>
                  </div>
                  {combinedHoldingsBreakdownLoading ? (
                    <LoadingSpinner size="sm" text="Loading..." />
                  ) : combinedCountryData.length > 0 ? (() => {
                    const max = combinedCountryData[0]?.value ?? 1;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {combinedCountryData.map(item => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '160px', flexShrink: 0, fontSize: '0.82rem', color: '#52525b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
                            <div style={{ flex: 1, height: '6px', background: '#f4f4f5', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${max > 0 ? (item.value / max) * 100 : 0}%`, height: '100%', background: '#000537', borderRadius: '3px', transition: 'width 0.4s ease' }} />
                            </div>
                            <span style={{ width: '48px', flexShrink: 0, textAlign: 'right', fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{(item.value * 100).toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    );
                  })() : (
                    <div style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>No region data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ETF investments section */}
      {activeTab === 'etfs' && (
        <>
          <ETFInvestmentsSection
            portfolio={portfolio}
            etfs={etfs}
            totalEtfValue={totalEtfValue}
            etfAllocationData={etfAllocationData}
            {...etfManagement}
            isinLookup={isinLookup}
          />
          <TypeSpecificDiversificationSection
            investmentType="etfs"
            top5IndustryData={etfIndustryData}
            top5CountryData={etfCountryData}
            isLoading={etfHoldingsBreakdownLoading}
          />
          <ETFOverlapSection
            overlapData={etfOverlapData}
            isLoading={etfOverlapLoading}
          />
        </>
      )}

      {/* Direct asset investments section */}
      {activeTab === 'assets' && (
        <>
          <DirectInvestmentsSection
            portfolio={portfolio}
            assets={assets}
            totalAssetValue={totalAssetValue}
            assetAllocationData={assetAllocationData}
            {...assetManagement}
            isinLookup={isinLookup}
          />
          <TypeSpecificDiversificationSection
            investmentType="assets"
            top5IndustryData={assetIndustryData}
            top5CountryData={assetCountryData}
            isLoading={assetHoldingsBreakdownLoading}
          />
        </>
      )}

      {/* Performance tab */}
      {activeTab === 'performance' && (
        <PerformanceSection portfolioId={portfolioId} />
      )}

      {/* Analytics tab — charts on top, then metrics, risk+correlation row,
          market cap, and the full-width sector table at the bottom */}
      {activeTab === 'analytics' && (
        <AnalyticsTab portfolioId={portfolioId} />
      )}

      {/* Ask tab — conversational Q&A over the portfolio (ROADMAP.md M3) */}
      {activeTab === 'chat' && (
        <ChatSection portfolioId={portfolioId} />
      )}
    </div>
  );
};

export default PortfolioDetail; 